import Skeleton from "../common/Skeleton";

function CellSkeleton({ lines = 1, withAvatar = false, align = "left" }) {
  return (
    <div
      className={`flex items-center gap-3 ${
        align === "right" ? "justify-end" : "justify-start"
      }`}
    >
      {withAvatar && (
        <Skeleton className="h-9 w-9 shrink-0 rounded-xl bg-slate-200/90 dark:bg-slate-800/90" />
      )}

      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-4 w-[75%] rounded-md bg-slate-200/90 dark:bg-slate-800/90" />
        {lines > 1 && (
          <Skeleton className="h-3 w-[48%] rounded-md bg-slate-100 dark:bg-slate-800/60" />
        )}
      </div>
    </div>
  );
}

function StatPillSkeleton() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
      <Skeleton className="mb-2 h-3 w-16 rounded-md bg-slate-100 dark:bg-slate-800/60" />
      <Skeleton className="h-5 w-10 rounded-md bg-slate-200 dark:bg-slate-800" />
    </div>
  );
}

export default function TableLoader({
  columns = 6,
  rows = 6,
  showSearch = true,
  showAction = true,
  showStats = true,
  showFilters = true,
}) {
  return (
    <div className="mt-6 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_12px_40px_rgba(0,0,0,0.35)]">
      {/* Top workspace header */}
      <div className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white px-6 py-5 dark:border-slate-800 dark:from-slate-900 dark:to-slate-900">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="space-y-3">
            <Skeleton className="h-7 w-44 rounded-lg bg-slate-200 dark:bg-slate-800" />
            <Skeleton className="h-4 w-72 rounded-lg bg-slate-100 dark:bg-slate-800/60" />
          </div>

          <div className="flex w-full flex-col gap-3 sm:flex-row xl:w-auto">
            {showSearch && (
              <div className="relative w-full sm:w-[320px]">
                <Skeleton className="h-11 w-full rounded-2xl bg-slate-100 dark:bg-slate-800/70" />
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                  <Skeleton className="h-4 w-4 rounded-full bg-slate-200 dark:bg-slate-700" />
                </div>
              </div>
            )}

            {showAction && (
              <Skeleton className="h-11 w-full rounded-2xl bg-slate-900 dark:bg-white sm:w-36" />
            )}
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Skeleton className="h-9 w-24 rounded-full bg-slate-200 dark:bg-slate-800" />
            <Skeleton className="h-9 w-28 rounded-full bg-slate-100 dark:bg-slate-800/60" />
            <Skeleton className="h-9 w-20 rounded-full bg-slate-100 dark:bg-slate-800/60" />
            <Skeleton className="h-9 w-24 rounded-full bg-slate-100 dark:bg-slate-800/60" />
          </div>
        )}
      </div>

      {/* KPI strip */}
      {showStats && (
        <div className="grid grid-cols-2 gap-3 border-b border-slate-200 bg-slate-50/60 px-6 py-4 md:grid-cols-4 dark:border-slate-800 dark:bg-slate-950/30">
          <StatPillSkeleton />
          <StatPillSkeleton />
          <StatPillSkeleton />
          <StatPillSkeleton />
        </div>
      )}

      {/* Data grid */}
      <div className="px-6 py-5">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-separate border-spacing-0">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-950/40">
                  {[...Array(columns)].map((_, i) => (
                    <th
                      key={i}
                      className="border-b border-slate-200 px-4 py-4 text-left dark:border-slate-800"
                    >
                      <Skeleton className="h-4 w-20 rounded-md bg-slate-200 dark:bg-slate-800" />
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {[...Array(rows)].map((_, r) => (
                  <tr key={r} className="group">
                    {[...Array(columns)].map((_, c) => (
                      <td
                        key={c}
                        className="border-b border-slate-200 px-4 py-4 align-middle dark:border-slate-800"
                      >
                        {c === 0 ? (
                          <CellSkeleton withAvatar lines={2} />
                        ) : c === columns - 1 ? (
                          <div className="flex items-center justify-end gap-2">
                            <Skeleton className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800/70" />
                            <Skeleton className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800/70" />
                            <Skeleton className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800/70" />
                          </div>
                        ) : c % 3 === 0 ? (
                          <div className="flex items-center gap-2">
                            <Skeleton className="h-6 w-16 rounded-full bg-slate-200 dark:bg-slate-800" />
                          </div>
                        ) : c % 2 === 0 ? (
                          <CellSkeleton lines={2} />
                        ) : (
                          <CellSkeleton lines={1} />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer controls */}
        <div className="mt-4 flex flex-col gap-4 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800">
          <div className="flex items-center gap-3">
            <Skeleton className="h-4 w-32 rounded-md bg-slate-100 dark:bg-slate-800/60" />
            <Skeleton className="h-9 w-20 rounded-xl bg-slate-200 dark:bg-slate-800" />
          </div>

          <div className="flex items-center gap-2 self-end sm:self-auto">
            <Skeleton className="h-10 w-24 rounded-xl bg-slate-100 dark:bg-slate-800/70" />
            <Skeleton className="h-8 w-10 rounded-md bg-slate-200 dark:bg-slate-800" />
            <Skeleton className="h-10 w-24 rounded-xl bg-slate-100 dark:bg-slate-800/70" />
          </div>
        </div>
      </div>
    </div>
  );
}